// DocumentStep.tsx — step 'document-upload' del onboarding (vía documento).
// Qué hace: sube un PDF/DOCX/TXT, lo analiza (analyze-document) y precarga el
//   scanResult con el brandInfo extraído antes de pasar a manual-name.
// Qué exporta: DocumentStep.
// Quién lo usa: CreateWorkspaceModal.

import { RefObject } from 'react';
import { Button } from '../../Button';
import { apiClient } from '../../../services/apiClient';
import { WizardStep } from '../types';
import { HiddenInput, SourceChoiceGrid, SourceChoiceBtn, SourceIcon, SourceLabel, SourceDesc } from '../styled';

interface Props {
  docFileRef: RefObject<HTMLInputElement>;
  docFile: File | null;
  setDocFile: (f: File | null) => void;
  setScanResult: (r: import('../types').ScanResult) => void;
  setStep: React.Dispatch<React.SetStateAction<WizardStep>>;
  goBack: () => void;
  pushStep: (next: WizardStep) => void;
  user: (text: string) => void;
  novah: (text: string) => void;
}

export function DocumentStep({
  docFileRef, docFile, setDocFile, setScanResult, setStep, goBack, pushStep, user, novah,
}: Props) {
  return (
    <>
      <HiddenInput ref={docFileRef} type="file" accept=".pdf,.docx,.txt,.doc" onChange={e => {
        const f = e.target.files?.[0];
        if (f) setDocFile(f);
      }} />

      {docFile && (
        <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, marginBottom: 8, fontSize: 13, color: '#166534' }}>
          {docFile.name} ({(docFile.size / 1024).toFixed(0)} KB)
        </div>
      )}

      <SourceChoiceGrid>
        <SourceChoiceBtn onClick={() => docFileRef.current?.click()}>
          <SourceIcon>📄</SourceIcon>
          <div>
            <SourceLabel>{docFile ? 'Change file' : 'Choose file'}</SourceLabel>
            <br /><SourceDesc>PDF, DOCX, or TXT</SourceDesc>
          </div>
        </SourceChoiceBtn>
      </SourceChoiceGrid>

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <Button type="button" $variant="ghost" $size="sm" onClick={() => {
          goBack();
          setDocFile(null);
        }} style={{ flex: 1 }}>Back</Button>
        <Button type="button" $size="sm" disabled={!docFile} onClick={async () => {
          if (!docFile) return;
          user(`Uploaded: ${docFile.name}`);
          novah("Analyzing your document...");
          pushStep('document-analyzing');

          try {
            const formData = new FormData();
            formData.append('file', docFile);

            const result = await apiClient.postFormData<{ brandInfo: Record<string, unknown> }>('/api/workspaces/analyze-document', formData);

            const info = result.brandInfo || {};
            // Store in scanResult so createFromScan can use it
            setScanResult({
              url: '',
              suggestedName: String(info.description || '').split('.')[0]?.substring(0, 50) || docFile.name.replace(/\.[^.]+$/, ''),
              images: [],
              brandColors: [],
              brandFonts: [],
              brandInfo: info,
            });

            const desc = String(info.description || 'your business').substring(0, 120);
            novah(`I analyzed your document! It seems to be about: ${desc}. What's the brand name?`);
            setStep('manual-name');
          } catch (err) {
            console.error('Document analysis failed:', err);
            novah("Sorry, I couldn't analyze that document. You can try a different file or enter your brand info manually.");
            setStep('source-choice');
          }
        }} style={{ flex: 1 }}>Analyze</Button>
      </div>
    </>
  );
}
